import React, { createContext, useReducer, useContext, useCallback, Dispatch } from 'react';
import { getQuestionCampaignById, getQuestionList, getRewardTotalPoints, saveAnswer } from '@/service/vip';
import { IQuestionInfo, IQuestion } from '@/components/AppreciationDay/index.d';
import { message } from '@/components/UI';

const handleDefAnswer = (item: IQuestion) => {
    const defAns: string[] = [];
    const userAns: string[] = [];
    const optionsArr = item.options ? item.options.split(',') : [];
    item.optionList &&
        item.optionList.map((optItem) => {
            if (optItem.correct) defAns.push(optItem.optionId);
            if (optionsArr.indexOf(optItem.optionSelect) > -1) userAns.push(optItem.optionId);
        });
    item.defAnswer = defAns.length > 0 ? defAns.join(',') : '';
    item.userAnswer = userAns.length > 0 ? userAns.join(',') : '';
    return item;
};

interface QuestionState {
    // 基础信息
    questionInfo: IQuestionInfo | null;
    // loading
    loading: boolean;
    // 当前作答序号
    index: number;
    // question列表
    question: IQuestion[];
    // 获取优惠券个数
    rewardCouponCount: number;
    // 获取积分总数
    rewardTotalPoints: number;
    // 场景，欧洲站点样式不一样
    scenario?: string;
}

type QuestionAction = {
    type: string;
    payload: any;
};

const initState: QuestionState = {
    questionInfo: null,
    loading: false,
    index: 0,
    question: [],
    rewardCouponCount: 0,
    rewardTotalPoints: 0,
    scenario: '',
};

const fsReducer = (state: QuestionState, action: QuestionAction): QuestionState => {
    const { type, payload } = action;
    switch (type) {
        case 'loading':
            return { ...state, loading: payload };
        case 'changeQuestionInfo':
            return { ...state, questionInfo: payload };
        case 'changeIndex':
            return { ...state, index: payload };
        case 'changeQuestion':
            return { ...state, question: payload };
        case 'changeReward':
            const { rewardCouponCount, rewardTotalPoints } = payload;
            return { ...state, rewardCouponCount, rewardTotalPoints };
        case 'changeScenario':
            return { ...state, scenario: payload };
        default:
            return state;
    }
};

export const QuestionContext = createContext<{ state: QuestionState; dispatch: React.Dispatch<QuestionAction> }>({
    state: initState,
    dispatch: () => null,
});

const asyncDispatch = (dispatch: Dispatch<QuestionAction>) =>
    useCallback(async ({ type, payload }: QuestionAction) => {
        switch (type) {
            case 'getQuestionInfo':
                if (!payload.id) return;
                try {
                    const res = await getQuestionCampaignById(payload.isEditStatus, { campaignId: payload.id }, payload.locale);
                    if (res.code !== '0') return message.error(res.message);
                    dispatch({
                        type: 'changeQuestionInfo',
                        payload: res.data,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            case 'getQuestionList':
                try {
                    dispatch({ type: 'loading', payload: true });
                    const { campaignId, questionGroupId, locale } = payload;
                    const res = await getQuestionList({ campaignId, questionGroupId }, locale);
                    dispatch({ type: 'loading', payload: false });
                    // 如果code为2422 更新questionInfo
                    if (res.code === '2422') {
                        const questionInfo = await getQuestionCampaignById(payload.isEditStatus, { campaignId: payload.id }, payload.locale);
                        dispatch({
                            type: 'changeQuestionInfo',
                            payload: questionInfo.data,
                        });
                    }
                    if (res.code !== '0') return message.error(res.message);
                    const { rewardCouponCount, rewardTotalPoints, questionList } = res.data;
                    // 给questionList添加defAnswer，userAnswer
                    questionList && questionList.map((item) => handleDefAnswer(item));
                    dispatch({
                        type: 'changeReward',
                        payload: { rewardCouponCount, rewardTotalPoints },
                    });
                    dispatch({
                        type: 'changeQuestion',
                        payload: questionList,
                    });
                } catch (err) {
                    dispatch({ type: 'loading', payload: false });
                    console.log(err);
                }
                return;
            case 'saveAnswer':
                try {
                    dispatch({ type: 'loading', payload: true });
                    const { data, locale, question, index } = payload;
                    const res = await saveAnswer(data, locale);
                    if (res.code !== '0') return message.error(res.message);
                    // 更新question列表
                    question[index] = handleDefAnswer(res.data);
                    dispatch({
                        type: 'changeQuestion',
                        payload: question,
                    });
                    // 更新获取分数
                    const pointsRes = await getRewardTotalPoints({ campaignId: data.campaignId, questionGroupId: data.questionGroupId }, locale);
                    if (pointsRes.code !== '0') return message.error(pointsRes.message);
                    const { rewardCouponCount, rewardTotalPoints } = pointsRes.data;
                    // dispatch({
                    //     type: 'changeReward',
                    //     payload: { rewardCouponCount, rewardTotalPoints },
                    // });
                    if (res?.data?.estimateRewardTotalPoints) {
                        dispatch({
                            type: 'changeReward',
                            payload: { rewardTotalPoints: res?.data?.estimateRewardTotalPoints, rewardCouponCount },
                        });
                    }
                } catch (err) {
                    console.log(err);
                }
                dispatch({ type: 'loading', payload: false });
                return;
            default:
                dispatch({ type, payload });
        }
    }, []);

export function QuestionProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(fsReducer, initState);
    return <QuestionContext.Provider value={{ state, dispatch: asyncDispatch(dispatch) }}>{children}</QuestionContext.Provider>;
}

export const useQuestionStore = () => {
    const store = useContext(QuestionContext);
    return store.state;
};

export const useQuestionDispatch = () => {
    const store = useContext(QuestionContext);
    return store.dispatch;
};
